import React, { useState } from 'react'
import Skeleton from 'components/skeleton'
import { useThemedColours } from 'hooks/useTheme'

export interface ITableData {
  label: any
  value: string | number | Date
}

export interface SortColumn {
  id: string
  ascending: boolean
}

interface TableProps {
  data: Array<Record<string, ITableData>>
  include: Array<any>
  defaultSort: string
  defaultSortAscending: boolean
  defaultSortColumns?: SortColumn[]
  sort?: boolean
  loading?: boolean
  highlightColumn?: string
  highlightColor?: string
  stickyHeader?: boolean
}

export const Table: React.FC<TableProps> = ({
  data,
  include,
  defaultSort,
  defaultSortAscending,
  defaultSortColumns,
  sort,
  loading,
  highlightColumn,
  highlightColor,
  stickyHeader = false
}: TableProps) => {
  const themed = useThemedColours()

  const buildDefaultSort = (): SortColumn[] =>
    defaultSortColumns && defaultSortColumns.length > 0
      ? defaultSortColumns.map((c) => ({ ...c }))
      : [{ id: defaultSort, ascending: defaultSortAscending }]

  const [sortColumns, setSortColumns] = useState<SortColumn[]>(buildDefaultSort)

  const compareValues = (av: string | number | Date, bv: string | number | Date): number => {
    const aIsNum =
      (typeof av === 'number' && Number.isFinite(av)) ||
      (typeof av === 'string' && av.trim() !== '' && Number.isFinite(Number(av)))
    const bIsNum =
      (typeof bv === 'number' && Number.isFinite(bv)) ||
      (typeof bv === 'string' && bv.trim() !== '' && Number.isFinite(Number(bv)))

    if (aIsNum && bIsNum) {
      const aNum = typeof av === 'number' ? av : Number(av)
      const bNum = typeof bv === 'number' ? bv : Number(bv)
      return aNum - bNum
    }

    const aStr = String(av)
    const bStr = String(bv)
    const aParsed = Date.parse(aStr)
    const bParsed = Date.parse(bStr)
    const aIsDate = !Number.isNaN(aParsed)
    const bIsDate = !Number.isNaN(bParsed)

    if (aIsDate && bIsDate) {
      return aParsed - bParsed
    }

    return aStr.toLowerCase().localeCompare(bStr.toLowerCase())
  }

  const rowSort = (a: Record<string, ITableData>, b: Record<string, ITableData>) => {
    for (const { id, ascending } of sortColumns) {
      const av = a[id]?.value ?? ''
      const bv = b[id]?.value ?? ''
      const dir = ascending ? 1 : -1
      const comparison = compareValues(av, bv)
      if (comparison !== 0) return dir * comparison
    }
    return 0
  }

  function handleClick(name: string, event: React.MouseEvent) {
    const existingIndex = sortColumns.findIndex((s) => s.id === name)

    if (event.shiftKey) {
      if (existingIndex >= 0) {
        const updated = [...sortColumns]
        updated[existingIndex].ascending = !updated[existingIndex].ascending
        setSortColumns(updated)
      } else {
        setSortColumns([...sortColumns, { id: name, ascending: true }])
      }
    } else {
      if (existingIndex >= 0) {
        const updated = [...sortColumns]
        updated[existingIndex].ascending = !updated[existingIndex].ascending
        setSortColumns(updated)
      } else {
        setSortColumns([{ id: name, ascending: true }])
      }
    }
  }

  const removeSort = (id: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setSortColumns(sortColumns.filter((s) => s.id !== id))
  }

  const clearAllSorts = () => {
    setSortColumns(buildDefaultSort())
  }

  const getCellBackgroundColor = (row: Record<string, ITableData>, columnId: string): string => {
    if (highlightColumn && columnId === highlightColumn) {
      return highlightColor || 'var(--color-light-blue)'
    }
    if (row['_rowBackgroundColor']) {
      return themed.fill(row['_rowBackgroundColor'].value as string) ?? 'transparent'
    }
    return 'transparent'
  }

  const getSortIndicator = (columnId: string) => {
    const index = sortColumns.findIndex((s) => s.id === columnId)
    if (index === -1) return null
    const { ascending } = sortColumns[index]
    return (
      <span className="text-sm bg-really-light-blue rounded inline-flex items-center ml-1.5 py-0.5 px-1.5">
        {sortColumns.length > 1 && <span className="mr-0.5">{index + 1}</span>}
        {ascending ? '↑' : '↓'}
        <span
          onClick={(e) => removeSort(columnId, e)}
          className="cursor-pointer ml-1 opacity-60"
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
        >
          ×
        </span>
      </span>
    )
  }

  const getColumnName = (id: string) => {
    const col = include.find((i) => i.id === id)
    return col?.header || id
  }

  const defaultSortState = buildDefaultSort()
  const hasCustomSort =
    sortColumns.length !== defaultSortState.length ||
    sortColumns.some((s, i) => s.id !== defaultSortState[i]?.id || s.ascending !== defaultSortState[i]?.ascending)

  return (
    <div className={`block ${stickyHeader ? '' : 'h-full overflow-auto'}`}>
      <div className="inline-block min-w-full">
        <table className="text-base-sm border-separate border-spacing-0 bg-transparent margin-0 text-body rounded w-full">
          <thead className={`select-none sticky ${stickyHeader ? 'top-0' : 'top-0'} z-[1]`}>
            <tr>
              <td colSpan={include.length} className="bg-surface py-2 border-b-2 border-divider">
                <div className="flex items-center gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span>Sorted by:</span>
                    {sortColumns.length === 0 && <span className="italic text-faint">None</span>}
                    {sortColumns.map((s, i) => (
                      <span
                        key={s.id}
                        className="bg-really-light-blue rounded inline-flex items-center gap-1 py-[3px] px-2"
                      >
                        {sortColumns.length > 1 && <span className="font-semibold">{i + 1}.</span>}
                        {getColumnName(s.id)} {s.ascending ? '↑' : '↓'}
                        <span
                          className="cursor-pointer ml-0.5 opacity-60"
                          onClick={(e) => removeSort(s.id, e)}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
                        >
                          ×
                        </span>
                      </span>
                    ))}
                    {hasCustomSort && (
                      <span
                        onClick={clearAllSorts}
                        className="cursor-pointer ml-1 text-purple"
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        Reset To Default
                      </span>
                    )}
                  </div>
                  <span className="text-faint text-[12px]">Shift+click to add columns</span>
                </div>
              </td>
            </tr>
            <tr className="h-10">
              {include.map(function (includeTerm, index) {
                const isSorted = sortColumns.some((s) => s.id === includeTerm.id)
                return (
                  <th
                    className={`text-left text-sm align-middle font-semibold border-b-2 border-divider bg-surface ${sort !== false && 'cursor-pointer'} p-1`}
                    key={index}
                    onClick={(e) => {
                      if (sort !== false) handleClick(includeTerm.id, e)
                    }}
                  >
                    {includeTerm.header}
                    {isSorted && getSortIndicator(includeTerm.id)}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr className="border-b border-divider-subtle h-[30px]">
                {include.map((_, index: number) => {
                  return (
                    <td key={index} className="text-left align-middle whitespace-nowrap p-2.5">
                      <Skeleton className="w-full mt-2.5 h-[30px]" />
                    </td>
                  )
                })}
              </tr>
            )}

            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={include.length} className="text-center text-muted p-2.5">
                  No data
                </td>
              </tr>
            )}
            {!loading &&
              data.length > 0 &&
              data.sort(rowSort).map((row, index) => {
                return (
                  <tr key={index} className="border-b border-divider h-[30px]">
                    {include.map((includeObject: { header: string; id: string }, index: number) => {
                      return (
                        <td
                          key={index}
                          className="text-left align-middle whitespace-nowrap p-2.5"
                          style={{ backgroundColor: getCellBackgroundColor(row, includeObject.id) }}
                        >
                          {row[includeObject.id].label}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
